// Top-down board diagram: target regions, where each foot landed (hollow) and
// where it ended up (filled), with arrows for steps — or a scatter of many
// landings for a session. Nose to the rider's left, toe-side rail at the bottom.

import { useState } from 'react';
import type { Adjustment, Placement } from '../../core/analysis';
import type { Vec2 } from '../../core/geometry';
import { boardHalfWidth, boardSpan, REFERENCE } from '../../core/board';
import { toeSideSign, type Stance } from '../../core/mapping';
import type { Reference } from '../../core/reference';

export interface ScatterPoint {
  front: Vec2;
  rear: Vec2;
  label: string;
}

interface Props {
  reference: Reference;
  stance: Stance;
  tol: { along: number; across: number };
  tape?: { stripLength: number; gap: number } | null;
  initial?: Placement | null;
  settled?: Placement | null;
  adjustments?: Adjustment[];
  scatter?: ScatterPoint[];
  /** Live feet (setup preview). */
  live?: { front: Vec2 | null; rear: Vec2 | null } | null;
  captures?: { front: Vec2; rear: Vec2 }[];
  compact?: boolean;
  title?: string;
}

const FOOT_LEN = 25;
const FOOT_W = 9.5;

export function BoardView(p: Props) {
  const { reference: ref, stance } = p;
  const s = toeSideSign(stance);
  const dir = s; // regular: nose drawn to the right
  // Draw a real 6′ board around the feet: back foot over the fins near the
  // tail, front foot around the middle (docs/STANCE.md).
  const { tail, nose } = boardSpan(ref.rear.centre.y);
  const midY = (tail + nose) / 2;
  const W = 360;
  const H = p.compact ? 120 : 150;
  const k = (W - 16) / (nose - tail);
  const cx = W / 2;
  const cy = H / 2 + 4;
  // Board plane (x across, y along) → screen.
  const X = (pt: Vec2) => cx + dir * (pt.y - midY) * k;
  const Y = (pt: Vec2) => cy + s * pt.x * k;
  const angleToScreen = (a: number) => {
    const d = { x: Math.cos(a), y: Math.sin(a) };
    return Math.atan2(s * d.x, dir * d.y);
  };
  const [hover, setHover] = useState<{ x: number; y: number; text: string } | null>(null);

  const boardPath = (() => {
    const n = 40;
    const side = (sign: 1 | -1) =>
      Array.from({ length: n + 1 }, (_, i) => {
        const t = sign === 1 ? i / n : 1 - i / n;
        const y = tail + (nose - tail) * t;
        const half = sign * boardHalfWidth(t);
        return `${X({ x: half, y })},${Y({ x: half, y })}`;
      });
    return `M${[...side(1), ...side(-1)].join(' L')} Z`;
  })();
  const fins = [
    { x: -15, y: tail + REFERENCE.sideFinFromTail },
    { x: 15, y: tail + REFERENCE.sideFinFromTail },
    { x: 0, y: tail + REFERENCE.centreFinFromTail },
  ];

  const foot = (c: Vec2, angle: number, role: 'front' | 'rear', variant: 'target' | 'initial' | 'settled' | 'live', key: string) => {
    const deg = (angleToScreen(angle) * 180) / Math.PI;
    const l = FOOT_LEN * k;
    const w = FOOT_W * k;
    const path = `M${-l / 2},0 C${-l / 2},${-w * 0.55} ${-l * 0.1},${-w * 0.62} ${l * 0.18},${-w * 0.58} C${l * 0.42},${-w * 0.55} ${l / 2},${-w * 0.35} ${l / 2},0 C${l / 2},${w * 0.35} ${l * 0.42},${w * 0.55} ${l * 0.18},${w * 0.58} C${-l * 0.1},${w * 0.62} ${-l / 2},${w * 0.55} ${-l / 2},0 Z`;
    const color = `var(--${role})`;
    const style =
      variant === 'target'
        ? { fill: 'none', stroke: 'var(--text-3)', strokeWidth: 1, strokeDasharray: '3 3' }
        : variant === 'initial'
          ? { fill: 'var(--surface)', stroke: color, strokeWidth: 2 }
          : variant === 'live'
            ? { fill: color, fillOpacity: 0.55, stroke: color, strokeWidth: 1.5 }
            : { fill: color, stroke: 'var(--surface)', strokeWidth: 2 };
    return <path key={key} d={path} transform={`translate(${X(c)},${Y(c)}) rotate(${deg})`} {...style} />;
  };

  const tolEllipse = (role: 'front' | 'rear') => {
    const c = ref[role].centre;
    return (
      <ellipse
        key={`t-${role}`}
        cx={X(c)}
        cy={Y(c)}
        rx={p.tol.along * k}
        ry={p.tol.across * k}
        fill={`var(--${role})`}
        fillOpacity={0.1}
        stroke={`var(--${role})`}
        strokeOpacity={0.7}
        strokeWidth={1}
      />
    );
  };

  const arrow = (a: Adjustment, i: number) => {
    const x1 = X(a.from);
    const y1 = Y(a.from);
    const x2 = X(a.to);
    const y2 = Y(a.to);
    const ang = Math.atan2(y2 - y1, x2 - x1);
    const h = 7;
    return (
      <g key={`adj-${i}`} stroke={`var(--${a.foot})`} strokeWidth={2} fill="none" strokeLinecap="round">
        <line x1={x1} y1={y1} x2={x2} y2={y2} />
        <path d={`M${x2 - h * Math.cos(ang - 0.45)},${y2 - h * Math.sin(ang - 0.45)} L${x2},${y2} L${x2 - h * Math.cos(ang + 0.45)},${y2 - h * Math.sin(ang + 0.45)}`} />
      </g>
    );
  };

  const steps = (p.adjustments ?? []).filter((a) => a.kind === 'step');

  return (
    <div className="chart" onPointerLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={p.title ?? 'Top-down view of your foot placement on a 6-foot reference board'}>
        <path d={boardPath} fill="var(--surface-2)" stroke="var(--hairline)" />
        <line x1={X({ x: 0, y: tail + 2 })} y1={cy} x2={X({ x: 0, y: nose - 2 })} y2={cy} stroke="var(--hairline)" strokeWidth={1} />
        {fins.map((f, i) => (
          <line key={`fin-${i}`} x1={X({ x: f.x, y: f.y - 5 })} y1={Y({ x: f.x, y: f.y - 5 })} x2={X({ x: f.x, y: f.y + 4 })} y2={Y({ x: f.x, y: f.y + 4 })} stroke="var(--text-3)" strokeWidth={2.5} strokeLinecap="round" />
        ))}
        {p.tape && (
          <g stroke="var(--accent)" strokeOpacity={0.35} strokeWidth={3}>
            <line x1={X({ x: -p.tape.stripLength / 2, y: 0 })} y1={Y({ x: -p.tape.stripLength / 2, y: 0 })} x2={X({ x: p.tape.stripLength / 2, y: 0 })} y2={Y({ x: p.tape.stripLength / 2, y: 0 })} />
            <line x1={X({ x: -p.tape.stripLength / 2, y: p.tape.gap })} y1={Y({ x: -p.tape.stripLength / 2, y: p.tape.gap })} x2={X({ x: p.tape.stripLength / 2, y: p.tape.gap })} y2={Y({ x: p.tape.stripLength / 2, y: p.tape.gap })} />
          </g>
        )}
        {tolEllipse('front')}
        {tolEllipse('rear')}
        {foot(ref.front.centre, ref.front.angle, 'front', 'target', 'tf')}
        {foot(ref.rear.centre, ref.rear.angle, 'rear', 'target', 'tr')}
        <text x={dir > 0 ? W - 6 : 6} y={14} textAnchor={dir > 0 ? 'end' : 'start'} fontSize={11} fill="var(--text-3)">
          {dir > 0 ? 'nose →' : '← nose'}
        </text>
        <text x={X({ x: 0, y: midY })} y={H - 3} textAnchor="middle" fontSize={10} fill="var(--text-3)">
          toe side
        </text>

        {p.captures?.map((c, i) => (
          <g key={`cap-${i}`}>
            <circle cx={X(c.front)} cy={Y(c.front)} r={3} fill="var(--front)" stroke="var(--surface)" strokeWidth={1.5} />
            <circle cx={X(c.rear)} cy={Y(c.rear)} r={3} fill="var(--rear)" stroke="var(--surface)" strokeWidth={1.5} />
          </g>
        ))}

        {p.scatter?.map((pt, i) =>
          (['front', 'rear'] as const).map((role) => (
            <g key={`sc-${i}-${role}`}>
              <circle cx={X(pt[role])} cy={Y(pt[role])} r={4.5} fill={`var(--${role})`} stroke="var(--surface)" strokeWidth={2} />
              <circle
                cx={X(pt[role])}
                cy={Y(pt[role])}
                r={12}
                fill="transparent"
                onPointerEnter={() => setHover({ x: X(pt[role]), y: Y(pt[role]), text: `${pt.label} · ${role === 'front' ? 'front' : 'back'} foot` })}
              />
            </g>
          )),
        )}

        {steps.map(arrow)}
        {p.settled && foot(p.settled.front.centre, p.settled.front.angle, 'front', 'settled', 'sf')}
        {p.settled && foot(p.settled.rear.centre, p.settled.rear.angle, 'rear', 'settled', 'sr')}
        {p.initial && foot(p.initial.front.centre, p.initial.front.angle, 'front', 'initial', 'if')}
        {p.initial && foot(p.initial.rear.centre, p.initial.rear.angle, 'rear', 'initial', 'ir')}
        {p.live?.front && foot(p.live.front, ref.front.angle, 'front', 'live', 'lf')}
        {p.live?.rear && foot(p.live.rear, ref.rear.angle, 'rear', 'live', 'lr')}
      </svg>
      {hover && (
        <div className="tooltip" style={{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / H) * 100}%` }}>
          {hover.text}
        </div>
      )}
      <div className="legend" style={{ marginTop: 6, display: p.compact ? 'none' : undefined }}>
        <span>
          <i className="dot front" /> Front foot
        </span>
        <span>
          <i className="dot rear" /> Back foot
        </span>
        {(p.initial || p.settled) && (
          <span className="faint">
            {p.initial ? 'outline = landing' : ''}
            {p.initial && p.settled ? ' · ' : ''}
            {p.settled ? 'filled = hold' : ''}
          </span>
        )}
        {p.scatter && <span className="faint">rings = target zones</span>}
      </div>
    </div>
  );
}
